import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { CalendarCheck, Store } from 'lucide-react';
import { toast } from 'react-toastify';
import { customerAnalyticsAPI } from '../../services';

/**
 * Tab "Khách quay lại theo chi nhánh" (#143552).
 *
 * Đếm theo NHÓM khách, không theo lượt. Hai khối tách riêng vì khác bản chất số liệu:
 *  - Khối A: nhóm ĐẶT BÀN quay lại (nguồn phiếu đặt bàn, bỏ phiếu đã huỷ).
 *  - Khối B: nhóm quay lại KHÔNG qua đặt bàn (nguồn đơn hoàn tất không gắn phiếu đặt bàn).
 * Cả 2 khối gộp thêm các SĐT từng dùng chung 1 thiết bị vào cùng 1 nhóm.
 */

const pct1 = (n) => `${((n ?? 0) * 100).toFixed(1)}%`;

function BlockTable({ title, icon: Icon, color, unit, block, note }) {
  if (!block) return null;
  const { thresholds = [], rows = [], total_row: totalRow, coverage } = block;
  const hasDeviceMerge =
    (totalRow?.groups_saved_by_device ?? 0) > 0 ||
    rows.some((r) => (r.groups_saved_by_device ?? 0) > 0);

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="py-2 px-3 d-flex align-items-center gap-2" style={{ background: color + '15' }}>
        <Icon size={16} color={color} />
        <strong style={{ fontSize: '0.9rem', color }}>{title}</strong>
        {block.device_grouping_applied && (
          <Badge bg="success" style={{ fontSize: '0.68rem' }}>đã gộp theo thiết bị</Badge>
        )}
      </Card.Header>
      <Card.Body>
        {/* AC2: ghi rõ cách đếm ngay trên màn, không để người xem phải hỏi lại */}
        <Alert variant="light" className="border py-2 px-3 small text-muted mb-3">
          {note}
        </Alert>

        {!rows.length ? (
          <div className="text-center text-muted py-4 small">Không có dữ liệu trong khoảng đã chọn</div>
        ) : (
          <Table size="sm" hover responsive className="mb-2">
            <thead>
              <tr className="table-light">
                <th>Chi nhánh</th>
                {thresholds.map((t) => (
                  <th key={t} className="text-end">≥{t} {unit}</th>
                ))}
                {hasDeviceMerge && (
                  <th className="text-end">Gộp thêm nhờ thiết bị</th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.branch_id}>
                  <td>{r.branch_name}</td>
                  {r.counts.map((c, i) => (
                    <td key={thresholds[i]} className="text-end">{c}</td>
                  ))}
                  {hasDeviceMerge && (
                    <td className="text-end text-success">
                      {r.groups_saved_by_device > 0 ? `−${r.groups_saved_by_device} nhóm` : '—'}
                    </td>
                  )}
                </tr>
              ))}
              {totalRow && (
                <tr className="fw-bold border-top border-dark">
                  <td>{totalRow.branch_name}</td>
                  {totalRow.counts.map((c, i) => (
                    <td key={thresholds[i]} className="text-end">{c}</td>
                  ))}
                  {hasDeviceMerge && (
                    <td className="text-end text-success">
                      {totalRow.groups_saved_by_device > 0
                        ? `−${totalRow.groups_saved_by_device} nhóm`
                        : '—'}
                    </td>
                  )}
                </tr>
              )}
            </tbody>
          </Table>
        )}

        {/* AC4: khoảng dữ liệu + phần không định danh được, tính động theo kỳ đang chọn */}
        {coverage && (
          <div className="text-muted" style={{ fontSize: '0.78rem' }}>
            Có SĐT: <strong>{coverage.with_phone}</strong>/{coverage.total_records} · Có thiết bị:{' '}
            <strong>{coverage.with_device}</strong>/{coverage.total_records} ·{' '}
            <strong>{pct1(coverage.unidentified_pct)}</strong> không có SĐT nên không đếm được
            (không phải 0 khách, mà là không xác định).
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default function RepeatCustomersTab({ queryParams }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customerAnalyticsAPI.getRepeatCustomers(queryParams);
      setData(res);
    } catch (err) {
      console.error(err);
      toast.error('Không tải được thống kê khách quay lại');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="text-center py-5">
        <Spinner />
        <div className="mt-2 text-muted small">Đang tính nhóm khách quay lại...</div>
      </div>
    );
  }

  const range = data?.data_range;

  return (
    <div data-testid="repeat-customers-tab">
      {data?.period && (
        <Alert variant="light" className="py-2 px-3 mb-3 border small text-muted">
          Kỳ đang xem: <strong>{data.period.from_date}</strong> → <strong>{data.period.to_date}</strong>.
          {range?.first_date && (
            <> Dữ liệu thực có trong kỳ: <strong>{range.first_date}</strong> – <strong>{range.last_date}</strong>.</>
          )}
          {!range?.first_date && <> Kỳ này chưa có dữ liệu.</>}
        </Alert>
      )}

      <BlockTable
        title="Nhóm đặt bàn quay lại"
        icon={CalendarCheck}
        color="#0d6efd"
        unit="lần đặt"
        block={data?.reservation_block}
        note={
          <>
            Đếm theo <strong>NHÓM khách</strong> (không đếm theo lượt) — 1 nhóm đặt 5 lần vẫn tính 1
            nhóm. Nguồn: phiếu đặt bàn <strong>không tính phiếu đã huỷ</strong> (phiếu khách không
            đến vẫn tính vì khách đã đặt). Các số điện thoại từng dùng chung 1 thiết bị được gộp vào
            cùng 1 nhóm; phiếu do nhân viên hoặc Telegram tạo <strong>không có thiết bị</strong> nên
            phần đó vẫn chỉ gộp được theo số điện thoại.
          </>
        }
      />

      <BlockTable
        title="Nhóm quay lại KHÔNG qua đặt bàn"
        icon={Store}
        color="#198754"
        unit="lượt"
        block={data?.order_block}
        note={
          <>
            Đếm theo <strong>NHÓM khách</strong>, nguồn là đơn đã hoàn tất <em>không</em> gắn với
            phiếu đặt bàn nào (khách tới thẳng quán). Đã{' '}
            <strong>gộp các số điện thoại từng dùng chung 1 thiết bị</strong> vào 1 nhóm — 1 nhóm 2
            số điện thoại quay lại 5 lần vẫn chỉ tính 1 nhóm.
          </>
        }
      />

      {data?.device_grouping && (
        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
          Cách gộp thiết bị: bỏ qua thiết bị dùng chung dính quá{' '}
          {data.device_grouping.max_phones_per_device} số điện thoại (đã bỏ{' '}
          {data.device_grouping.skipped_shared_devices} thiết bị, ví dụ máy đặt tại quán) để không
          gộp nhầm nhiều khách thành 1 nhóm.
        </div>
      )}
    </div>
  );
}
